/**
 * When the globe turns, and when it holds still.
 *
 * Pulled out of the render loop because "does it rotate this frame?" is the one
 * rule in the globe with real behaviour in it, and inside a
 * `requestAnimationFrame` callback it cannot be tested at all.
 *
 * Three states, in priority order:
 *
 *   DRAGGING — the pointer owns the rotation; this function is not consulted.
 *   HOVERING — the pointer is on a customer. The globe FREEZES, including
 *              mid-flick inertia. Auto-rotate carries a marker out from under
 *              the cursor in well under a second, so reading a tooltip meant
 *              chasing it and clicking through meant hitting a moving target.
 *   FREE     — inertia decays to a stop, then auto-rotate resumes after a beat.
 *
 * A frozen frame keeps the stored velocity rather than zeroing it, so a marker
 * caught mid-throw stops dead and the throw picks up again when the pointer
 * moves away. Zeroing it would make every hover silently cancel the gesture.
 */

export interface RotationState {
  /** [lon, lat] in degrees. */
  rotation: readonly [number, number]
  /** Degrees per frame, [lon, lat]. */
  velocity: readonly [number, number]
  /** True while the pointer is on a customer marker. */
  hovering: boolean
  /** Milliseconds since the last drag, wheel or flick. */
  idleMs: number
}

/** Below this, a flick has died and auto-rotate may take over. */
const REST_SPEED = 0.01

/** Friction per frame. */
const DECAY = 0.955

/** How long after an interaction auto-rotate resumes. */
export const AUTO_ROTATE_DELAY_MS = 1500

/** Degrees of longitude per frame while auto-rotating. */
export const AUTO_ROTATE_STEP = 0.18

/** Web Mercator and the orthographic projection both give up past here. */
const MAX_LATITUDE = 85

export interface RotationStep {
  rotation: [number, number]
  velocity: [number, number]
  /** True when this frame left the globe exactly where it was. */
  frozen: boolean
  /** True when the interaction clock should be pushed forward (inertia is still running). */
  keepAwake: boolean
}

/** One frame of globe physics. Pure: same input, same output, no clock. */
export function stepRotation(state: RotationState): RotationStep {
  const [lon, lat] = state.rotation
  const [vx, vy] = state.velocity

  if (state.hovering) {
    // Hold everything, velocity included — see the header.
    return { rotation: [lon, lat], velocity: [vx, vy], frozen: true, keepAwake: false }
  }

  const speed = Math.hypot(vx, vy)
  if (speed > REST_SPEED) {
    return {
      rotation: [wrapLongitude(lon + vx), clampLatitude(lat + vy)],
      velocity: [vx * DECAY, vy * DECAY],
      frozen: false,
      // Auto-rotate must wait for the flick to die, or the two fight.
      keepAwake: true,
    }
  }

  if (state.idleMs > AUTO_ROTATE_DELAY_MS) {
    return {
      rotation: [wrapLongitude(lon + AUTO_ROTATE_STEP), lat],
      velocity: [0, 0],
      frozen: false,
      keepAwake: false,
    }
  }

  return { rotation: [lon, lat], velocity: [0, 0], frozen: true, keepAwake: false }
}

function wrapLongitude(lon: number): number {
  return lon % 360
}

function clampLatitude(lat: number): number {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat))
}
